import { api } from '../lib/api';

/**
 * Web Push registration for the PWA (SPEC.md §5.6).
 *
 * Every step here can legitimately fail on a cheap Android in a basement — no service
 * worker, permission denied, no network — so each returns a typed outcome rather than
 * throwing. Push is an enhancement; losing it must never break the app.
 */

export type PushSetupResult =
  | { status: 'subscribed'; endpoint: string }
  | { status: 'unsupported' }
  | { status: 'permission-denied' }
  | { status: 'no-vapid-key' }
  | { status: 'failed'; error: string };

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * What alerts are actually doing on this device, read without prompting for anything.
 *
 * A union rather than a permission string with a `supported` flag beside it, because
 * "this browser has no Web Push" and "this browser has it and the answer is no" lead
 * to completely different copy, and a caller that can forget to check the flag will.
 */
export type PushEnrolment =
  | { supported: false }
  | { supported: true; permission: NotificationPermission; subscribed: boolean };

/**
 * Reads the enrolment state. Never prompts, never subscribes, never throws.
 *
 * `permission` alone is not the answer: a device can hold a granted permission with no
 * subscription behind it — permission survives a subscription being revoked, an app
 * being reinstalled, or a push service rotating an endpoint — and in that state the
 * user is told nothing while the UI would happily claim alerts are on.
 */
export async function readPushEnrolment(): Promise<PushEnrolment> {
  if (!isPushSupported()) return { supported: false };

  const permission = Notification.permission;

  // `getRegistration`, not `.ready`: `ready` never settles when no worker is registered
  // at all, and this runs on render rather than behind a tap. A promise that hangs here
  // would leave the banner permanently blank instead of merely wrong.
  const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  if (!registration) return { supported: true, permission, subscribed: false };

  const subscription = await registration.pushManager.getSubscription().catch(() => null);
  return { supported: true, permission, subscribed: subscription !== null };
}

/**
 * Converts the URL-safe base64 VAPID key the server publishes into the Uint8Array
 * that pushManager.subscribe() demands.
 *
 * The return type is pinned to `Uint8Array<ArrayBuffer>`: `applicationServerKey`
 * accepts only an ArrayBuffer-backed view, and the default `ArrayBufferLike` also
 * admits SharedArrayBuffer, which the DOM types reject.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);

  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/** Best-effort human label so staff can identify a device in a revoke list. */
export function describeDevice(userAgent: string = navigator.userAgent): string {
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Firefox\//.test(userAgent)
          ? 'Firefox'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : 'Browser';

  const platform = /Android/.test(userAgent)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(userAgent)
      ? 'iOS'
      : /Windows/.test(userAgent)
        ? 'Windows'
        : /Mac OS X/.test(userAgent)
          ? 'macOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : 'device';

  return `${browser} on ${platform}`;
}

/**
 * Null means the server has no key configured, which is a real and reportable state.
 * A network failure is not that, and is left to throw so it surfaces as `failed`
 * rather than as a misleading "this server cannot send alerts".
 */
async function fetchVapidPublicKey(): Promise<string | null> {
  const { publicKey } = await api.pushPublicKey();
  return publicKey;
}

/**
 * Requests permission, subscribes, and registers the subscription with the API.
 *
 * Must be called from a user gesture: browsers reject a permission prompt that a
 * page asks for on load, and a technician should be choosing to enable alerts anyway.
 */
export async function enablePushNotifications(): Promise<PushSetupResult> {
  if (!isPushSupported()) return { status: 'unsupported' };

  try {
    // Ask only when there is something to ask. Permission outlives the subscription it
    // was granted for, so switching alerts back on after choosing off must not raise the
    // system dialog again — and on a `denied` device asking is pointless: the browser
    // will not show a prompt, it just answers `denied` again. Reading the property here
    // is what makes "no prompt the second time" a guarantee rather than a browser habit.
    const permission =
      Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;
    if (permission !== 'granted') return { status: 'permission-denied' };

    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) return { status: 'no-vapid-key' };

    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        // Chrome refuses silent pushes; every message must show a notification.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    await registerSubscription(subscription);
    return { status: 'subscribed', endpoint: subscription.endpoint };
  } catch (error) {
    return { status: 'failed', error: error instanceof Error ? error.message : String(error) };
  }
}

async function registerSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};

  // Throws an ApiError on refusal, which the caller turns into `failed` carrying the
  // server's own message rather than a bare status code nobody can act on.
  await api.subscribePush({
    endpoint: subscription.endpoint,
    keys: { p256dh: keys.p256dh ?? '', auth: keys.auth ?? '' },
    deviceName: describeDevice(),
  });
}

/** Unsubscribes this device locally and tells the server to forget the endpoint. */
export async function disablePushNotifications(): Promise<boolean> {
  if (!isPushSupported()) return false;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;

  // Tell the server first: if the browser drops it and the call then fails, the
  // server would keep pushing to a dead endpoint until a 410 prunes it.
  await api.unsubscribePush(subscription.endpoint).catch(() => undefined);

  return subscription.unsubscribe();
}
